import { useContext, useState, useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  Switch,
} from "react-router-dom";
import { useMutation } from "@apollo/client";
import { UserContext } from "./Contexts/UserContext";
import Login from "./Components/Login/Login";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import SingleAddressMap from "./Components/Maps/SingleAddressMap";
// import PropertyEdit from './Components/PropertyEdit'
import TabsWrapper from "./Components/TabsWrapper";
import withClearCache from "./ClearCache";
import { darkTheme, lightTheme } from "./Styles/styles";
import { ThemeProvider } from "@material-ui/core/styles";
import { useCookies } from "react-cookie";
import DashboardMap from "./Components/Maps/DashboardMap";
import IntentToSuspendPreview from "./Components/TicketAndWarning/IntentToSuspend/IntentToSuspendPreview";
import { UPDATE_GEOLOCATION } from "./Mutations/UpdateGeolocation";
import { Snackbar } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MuiAlert from "@material-ui/lab/Alert";
import { localGetData } from "./utils/utils";
import { DashboardContext } from "./Contexts/DashboardContext";
import { useServerEndpointCleanup } from "./CustomHooks/useServerEndpointCleanup";
import useChangeTabForTimeout from "./CustomHooks/useChangeTabForTimeout";
import { traveledAtLeast } from "./Components/Maps/mapUtils";
// import VCVC from './Components/VCVC/VCVC';

import moment from "moment";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
}));

const MainApp = props => {
  const classes = useStyles();
  const { user, setUser } = useContext(UserContext);
  const [ready, setReady] = useState(false);
  const topLevelRef = useRef(null);
  const [cookies, , removeCookie] = useCookies();
  const { indexedDb } = useContext(DashboardContext);
  useServerEndpointCleanup({
    apiHost: import.meta.env.VITE_API_HOST,
    cookies,
    removeCookie,
    indexedDb,
    setUser,
  });
  const [pausePolling, setPausePolling] = useState(false);
  const [updateGeolocation] = useMutation(UPDATE_GEOLOCATION);
  const [lastGeoUpdate, setLastGeoUpdate] = useState(null);

  const [popper, setPopper] = useState({ open: false, content: "" });

  const clientTimeout = +user?.inactivityTimeout || 1800;
  //warn user 5 minutes before timeout
  const timeoutWarning = 300;
  useChangeTabForTimeout(
    props?.timestamp,
    +clientTimeout,
    timeoutWarning,
    !!user?.isAuthenticated,
  );

  useEffect(() => {
    if (popper?.open) {
      const timer = setInterval(() => {
        setPopper({ open: false, content: "" });
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [popper]);

  useEffect(() => {
    if ((user?.id < 0 || user?.id === null) && localGetData("mp_user")) {
      setUser(localGetData("mp_user"));
    }
    setReady(true);
  }, [user?.id, setUser]);

  useEffect(() => {
    document.title = "Valcour Mobile - Dashboard";
  }, []);

  useEffect(() => {
    if (import.meta.env.VITE_DISABLE_EXIT_CONFIRMATION === "true") {
      window.addEventListener("beforeunload", event => {
        event.returnValue = "Are you sure you want to leave?";
      });
    }
  });
  function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
  }

  //For testing only
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (!pausePolling && user?.id >= 0 && user?.onDuty) {
  //       console.log("Testing geolocation update");
  //       updateGeolocation({
  //         variables: {
  //           input: {
  //             originatorId: user?.id,
  //             officerId: user?.id,
  //             lat: parseFloat(44 + Math.random()),
  //             lng: parseFloat(-73 - Math.random()),
  //             noSessionReset: true,
  //             accuracy: Math.round(Math.random() * 20) + 5, // Random accuracy between 5 and 25 meters
  //           },
  //         },
  //       });
  //       geoSuccess();
  //     } else {
  //       console.log("Not polling");
  //     }
  //   }, 2000);
  //   return () => {
  //     clearInterval(interval);
  //   };
  // });
  const userPositionRef = useRef(null);
  const geoSuccess = useCallback(
    data => {
      const now = moment();
      if (!lastGeoUpdate || now?.diff(lastGeoUpdate) > 5000) {
        const accuracy = Math.round(+data?.coords?.accuracy)
          ? Math.round(+data?.coords?.accuracy)
          : null;
        // if(accuracy && accuracy < 3200){
        const lat = parseFloat(data.coords?.latitude);
        const lng = parseFloat(data.coords?.longitude);

        //if the distance between the last location and the new location is less than 1 meter, do not update
        const prev = userPositionRef.current;
        const shouldUpdate = traveledAtLeast(
          lat,
          lng,
          prev?.lat,
          prev?.lng,
          1, // 1 meter threshold
        );

        if (shouldUpdate || !prev?.lat || !prev?.lng) {
          !pausePolling &&
            updateGeolocation({
              variables: {
                input: {
                  originatorId: user?.id,
                  officerId: user?.id,
                  lat: parseFloat(data?.coords?.latitude),
                  lng: parseFloat(data?.coords?.longitude),
                  accuracy: accuracy,
                  noSessionReset: true,
                },
              },
            });
          setLastGeoUpdate(now);
          userPositionRef.current = { lat, lng };
        } else {
          // console.log("The distance traveled was below 1 meter. ");
        }
      } else {
        // console.log('It has not been five seconds since your last location update')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastGeoUpdate, pausePolling, user?.id],
  );

  const geoError = () => {
    // console.error("geolocation error", error);
  };
  const geoOptions = {
    enableHighAccuracy: false,
  };

  // add an id
  useEffect(() => {
    if (
      user?.id >= 0 &&
      user?.onDuty &&
      user?.ori?.moduleOptions?.find(mo => mo?.name === "AVL Option") &&
      !pausePolling
    ) {
      // console.log('Using watchPosition')
      const geo = navigator.geolocation;
      if (!geo) {
        alert(
          "Your agency has the AVL option enabled but your location is being blocked by browser settings.",
        );
        return;
      }
      // console.log('Watching position for user', user)
      const watcher = geo.watchPosition(geoSuccess, geoError, geoOptions);
      return () => geo.clearWatch(watcher);
    } else {
      // console.log('Not watching position')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    user?.onDuty,
    pausePolling,
    user?.ori?.moduleOptions,
    geoOptions,
  ]);

  if (ready) {
    return (
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <Router>
          <ThemeProvider
            theme={
              cookies[`mp-userId-${user?.id}-night-mode`] === "true"
                ? darkTheme
                : lightTheme
            }
          >
            <div
              ref={topLevelRef}
              id="app"
              className={classes.root}
              style={{
                height: "100%",
                width: "100%",
                backgroundColor:
                  cookies[`mp-userId-${user?.id}-night-mode`] === "true"
                    ? "#616161"
                    : "#fff",
              }}
            >
              <Switch>
                <Route exact path="/mp/">
                  <Login {...props} />
                </Route>

                {/********* These are for testing ************/}
                {import.meta.env.DEV && (
                  <Route exact path="/mp/intent-to-suspend-preview">
                    <IntentToSuspendPreview />
                  </Route>
                )}
                {/* <Route exact path="/mp/vcvc">
                  <VCVC
                    ticket={WARNING_EXAMPLE}
                    showDialog={false}
                    bac={""}
                    defendantCopy={false}
                    isWarning={true}
                  />
                </Route> */}
                {/* ************************************** */}

                {/* 
                <PrivateRoute exact path="/mp/addresses/:addressId/edit">
                  <AddressContacts />
                </PrivateRoute> 
                {/* <PrivateRoute exact path="/mp/properties/edit/:propertyId/:incidentId">
                <PropertyEdit />
              </PrivateRoute> */}
                <PrivateRoute
                  exact
                  path="/mp/dashboard-map/:userId/:homeOriId/:oriIds/:incidentId?"
                >
                  <DashboardMap
                    subscriptionServer={props.subscriptionServer}
                    avlEnabled={
                      !!user?.ori?.moduleOptions?.find(
                        mo => mo?.name === "AVL Option",
                      )
                    }
                    user={user}
                  />
                </PrivateRoute>
                <PrivateRoute
                  exact
                  path="/mp/map/address/:addressId/:incidentId"
                >
                  <SingleAddressMap
                    avlEnabled={
                      !!user?.ori?.moduleOptions?.find(
                        mo => mo?.name === "AVL Option",
                      )
                    }
                    user={user}
                    subscriptionServer={props.subscriptionServer}
                  />
                </PrivateRoute>
                <PrivateRoute exact path="/mp/home">
                  <>
                    <TabsWrapper
                      timestamp={props.timestamp}
                      currentVersionDate={props.currentVersionDate}
                      setPopper={setPopper}
                      subscriptionServer={props.subscriptionServer}
                      setPausePolling={setPausePolling}
                      pausePolling={pausePolling}
                      socketConnected={props.socketConnected}
                    />
                  </>
                </PrivateRoute>
                <Route exact path="/">
                  <Redirect
                    to={{
                      pathname: "/mp",
                    }}
                  />
                </Route>
                <Route exact path="*">
                  <div>Page not found</div>
                </Route>
              </Switch>
            </div>
          </ThemeProvider>
        </Router>
        <Snackbar
          sx={{ width: 100 }}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={popper?.open}
          autoHideDuration={5000}
        >
          <Alert severity={popper?.severity}>
            <span style={{ whiteSpace: "pre-line" }}>{popper?.content}</span>
          </Alert>
        </Snackbar>
      </MuiPickersUtilsProvider>
    );
  } else {
    return <div>Loading...</div>;
  }
};

function PrivateRoute({ children, ...rest }) {
  const localStorageUser = localGetData("mp_user");
  const { user, setUser } = useContext(UserContext);
  if ((user?.id < 0 || user?.id === null) && localStorageUser) {
    setUser(localStorageUser);
  }
  return (
    <Route
      {...rest}
      render={({ location }) =>
        user?.isAuthenticated ||
        (localStorageUser && localStorageUser.isAuthenticated) ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/mp",
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}

const ClearCacheComponent = withClearCache(MainApp);

function App(props) {
  const { user, onlineState } = useContext(UserContext);

  return (
    <ClearCacheComponent online={onlineState} userId={user?.id} {...props} />
  );
}

export default App;
